import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthAttributionModel {
  id: string;
  name: string;
  modelType: string;
  windowDays: number;
  rulesJson: string;
  status: string;
  createdAtUtc: string;
}

export interface GrowthAttributionRun {
  id: string;
  attributionModelId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  status: string;
  revenueSourceMode: string;
  totalAttributedRevenue: number;
  ufcReconcileDelta?: number | null;
  findingsJson: string;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  idempotencyKey: string;
}

export interface GrowthAttributionTouchpoint {
  id: string;
  channelKey: string;
  touchType: string;
  occurredAtUtc: string;
  weight: number;
  revenueAmount?: number | null;
  partyId?: string | null;
  campaignId?: string | null;
  contentAssetId?: string | null;
}

export interface GrowthFunnelDefinition {
  id: string;
  funnelKey: string;
  name: string;
  stageDefinitionsJson: string;
  status: string;
}

export interface GrowthFunnelSnapshot {
  id: string;
  funnelDefinitionId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  stageMetricsJson: string;
  dropOffJson: string;
  bottleneckSummary: string;
  createdAtUtc: string;
}

export interface GrowthCohortDefinition {
  id: string;
  cohortKey: string;
  name: string;
  cohortRuleJson: string;
  status: string;
}

export interface GrowthCohortSnapshot {
  id: string;
  cohortDefinitionId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  retentionCurveJson: string;
  revenueCurveJson: string;
  createdAtUtc: string;
}

export interface GrowthClvScore {
  id: string;
  partyId: string;
  score: number;
  currencyCode: string;
  estimatedValue: number;
  modelVersion: string;
  explainabilityJson: string;
  computedAtUtc: string;
}

export interface GrowthChurnScore {
  id: string;
  partyId: string;
  score: number;
  riskBand: string;
  reasonCodesJson: string;
  explainabilityJson: string;
  computedAtUtc: string;
}

export interface GrowthForecastRun {
  id: string;
  forecastType: string;
  periodStartUtc: string;
  periodEndUtc: string;
  horizonDays: number;
  scenarioJson: string;
  forecastJson: string;
  confidenceBandJson: string;
  status: string;
  idempotencyKey: string;
  createdAtUtc: string;
}

export interface GrowthContentPerformanceScorecard {
  id: string;
  contentAssetId: string;
  campaignId?: string | null;
  channelKey?: string | null;
  periodStartUtc: string;
  periodEndUtc: string;
  viewCount: number;
  clickCount: number;
  leadCount: number;
  conversionRate: number;
  roiProxy?: number | null;
  score: number;
}

export interface GrowthIntelligencePreferenceProfile {
  id: string;
  segmentId?: string | null;
  partyId?: string | null;
  periodStartUtc: string;
  periodEndUtc: string;
  contentTypeScoresJson: string;
  channelScoresJson: string;
  recommendationJson: string;
}

export interface GrowthAudienceNeedSignal {
  id: string;
  segmentId?: string | null;
  contentAssetId?: string | null;
  sourceType: string;
  needTag: string;
  confidence: number;
  citationRefsJson: string;
  evidenceJson: string;
  createdAtUtc: string;
}

export interface GrowthReadModelSnapshot {
  id: string;
  snapshotKey: string;
  scopeType: string;
  payloadJson: string;
  refreshedAtUtc: string;
}

@Injectable({ providedIn: 'root' })
export class GrowthIntelligenceService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/intelligence`;

  listAttributionModels(): Observable<GrowthAttributionModel[]> {
    return this.http.get<GrowthAttributionModel[]>(`${this.base}/attribution/models`);
  }

  createAttributionModel(body: { name: string; modelType: string; windowDays: number; rulesJson: string; status: string }): Observable<GrowthAttributionModel> {
    return this.http.post<GrowthAttributionModel>(`${this.base}/attribution/models`, body);
  }

  runAttribution(body: {
    attributionModelId: string;
    periodStartUtc: string;
    periodEndUtc: string;
    revenueSourceMode: string;
    idempotencyKey: string;
    correlationId?: string | null;
  }): Observable<{ run: GrowthAttributionRun; isIdempotentReplay: boolean }> {
    return this.http.post<{ run: GrowthAttributionRun; isIdempotentReplay: boolean }>(`${this.base}/attribution/runs`, body);
  }

  listTouchpoints(filters: { runId?: string; partyId?: string; campaignId?: string }): Observable<GrowthAttributionTouchpoint[]> {
    let params = new HttpParams();
    if (filters.runId) params = params.set('runId', filters.runId);
    if (filters.partyId) params = params.set('partyId', filters.partyId);
    if (filters.campaignId) params = params.set('campaignId', filters.campaignId);
    return this.http.get<GrowthAttributionTouchpoint[]>(`${this.base}/attribution/touchpoints`, { params });
  }

  listFunnels(): Observable<GrowthFunnelDefinition[]> {
    return this.http.get<GrowthFunnelDefinition[]>(`${this.base}/funnels`);
  }

  upsertFunnel(body: { funnelKey?: string | null; name: string; stageDefinitionsJson: string; status: string }): Observable<GrowthFunnelDefinition> {
    return this.http.post<GrowthFunnelDefinition>(`${this.base}/funnels`, body);
  }

  refreshFunnel(id: string, body: { periodStartUtc: string; periodEndUtc: string }): Observable<GrowthFunnelSnapshot> {
    return this.http.post<GrowthFunnelSnapshot>(`${this.base}/funnels/${id}/refresh`, body);
  }

  listFunnelSnapshots(id: string): Observable<GrowthFunnelSnapshot[]> {
    return this.http.get<GrowthFunnelSnapshot[]>(`${this.base}/funnels/${id}/snapshots`);
  }

  listCohorts(): Observable<GrowthCohortDefinition[]> {
    return this.http.get<GrowthCohortDefinition[]>(`${this.base}/cohorts`);
  }

  upsertCohort(body: { cohortKey?: string | null; name: string; cohortRuleJson: string; status: string }): Observable<GrowthCohortDefinition> {
    return this.http.post<GrowthCohortDefinition>(`${this.base}/cohorts`, body);
  }

  refreshCohort(id: string, body: { periodStartUtc: string; periodEndUtc: string }): Observable<GrowthCohortSnapshot> {
    return this.http.post<GrowthCohortSnapshot>(`${this.base}/cohorts/${id}/refresh`, body);
  }

  listCohortSnapshots(id: string): Observable<GrowthCohortSnapshot[]> {
    return this.http.get<GrowthCohortSnapshot[]>(`${this.base}/cohorts/${id}/snapshots`);
  }

  computeClv(partyId: string): Observable<GrowthClvScore> {
    return this.http.post<GrowthClvScore>(`${this.base}/clv/${partyId}/compute`, {});
  }

  getClv(partyId: string): Observable<GrowthClvScore> {
    return this.http.get<GrowthClvScore>(`${this.base}/clv/${partyId}`);
  }

  computeChurn(partyId: string): Observable<GrowthChurnScore> {
    return this.http.post<GrowthChurnScore>(`${this.base}/churn/${partyId}/compute`, {});
  }

  getChurn(partyId: string): Observable<GrowthChurnScore> {
    return this.http.get<GrowthChurnScore>(`${this.base}/churn/${partyId}`);
  }

  runForecast(body: {
    forecastType: string;
    periodStartUtc: string;
    periodEndUtc: string;
    horizonDays: number;
    scenarioJson: string;
    idempotencyKey: string;
  }): Observable<{ run: GrowthForecastRun; isIdempotentReplay: boolean }> {
    return this.http.post<{ run: GrowthForecastRun; isIdempotentReplay: boolean }>(`${this.base}/forecast/runs`, body);
  }

  getForecastRun(id: string): Observable<GrowthForecastRun> {
    return this.http.get<GrowthForecastRun>(`${this.base}/forecast/runs/${id}`);
  }

  listContentPerformance(filters: { contentAssetId?: string; periodStart?: string; periodEnd?: string }): Observable<GrowthContentPerformanceScorecard[]> {
    let params = new HttpParams();
    if (filters.contentAssetId) params = params.set('contentAssetId', filters.contentAssetId);
    if (filters.periodStart) params = params.set('periodStart', filters.periodStart);
    if (filters.periodEnd) params = params.set('periodEnd', filters.periodEnd);
    return this.http.get<GrowthContentPerformanceScorecard[]>(`${this.base}/content/performance`, { params });
  }

  refreshContentPerformance(body: { periodStartUtc: string; periodEndUtc: string; contentAssetId?: string | null; campaignId?: string | null }): Observable<GrowthContentPerformanceScorecard[]> {
    return this.http.post<GrowthContentPerformanceScorecard[]>(`${this.base}/content/performance/refresh`, body);
  }

  getPreferenceProfile(filters: { segmentId?: string | null; partyId?: string | null }): Observable<GrowthIntelligencePreferenceProfile> {
    let params = new HttpParams();
    if (filters.segmentId) params = params.set('segmentId', filters.segmentId);
    if (filters.partyId) params = params.set('partyId', filters.partyId);
    return this.http.get<GrowthIntelligencePreferenceProfile>(`${this.base}/content/preference-profile`, { params });
  }

  refreshPreferenceProfile(body: { periodStartUtc: string; periodEndUtc: string; segmentId?: string | null; partyId?: string | null }): Observable<GrowthIntelligencePreferenceProfile> {
    return this.http.post<GrowthIntelligencePreferenceProfile>(`${this.base}/content/preference-profile/refresh`, body);
  }

  listAudienceNeeds(filters: { periodStart?: string; periodEnd?: string; segmentId?: string }): Observable<GrowthAudienceNeedSignal[]> {
    let params = new HttpParams();
    if (filters.periodStart) params = params.set('periodStart', filters.periodStart);
    if (filters.periodEnd) params = params.set('periodEnd', filters.periodEnd);
    if (filters.segmentId) params = params.set('segmentId', filters.segmentId);
    return this.http.get<GrowthAudienceNeedSignal[]>(`${this.base}/content/audience-needs`, { params });
  }

  refreshAudienceNeeds(body: { periodStartUtc: string; periodEndUtc: string; segmentId?: string | null }): Observable<GrowthAudienceNeedSignal[]> {
    return this.http.post<GrowthAudienceNeedSignal[]>(`${this.base}/content/audience-needs/refresh`, body);
  }

  getReadModel(snapshotKey: string): Observable<GrowthReadModelSnapshot> {
    return this.http.get<GrowthReadModelSnapshot>(`${this.base}/read-models/${snapshotKey}`);
  }
}

export function parseJsonRows<T>(json: string | null | undefined, fallback: T[] = []): T[] {
  if (!json?.trim()) return fallback;
  try {
    const parsed = JSON.parse(json) as T[];
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}
